import {
  Directive,
  forwardRef,
  input,
  InputSignal,
  effect
} from '@angular/core';
import { NG_VALIDATORS } from '@angular/forms';

import type {
  AbstractControl,
  ValidationErrors,
  Validator
} from '@angular/forms';

import type {
  Provider,
  Type
} from '@angular/core'
import type { SelectedFile } from './file-upload.component';

export const FILE_UPLOAD_MIN_VALIDATOR: Provider = {
  provide: NG_VALIDATORS,
  useExisting: forwardRef((): Type<FileUploadMinValidator> => FileUploadMinValidator),
  multi: true
};

/**
 * tdWbFileUploadMinValidator Directive
 *
 * Validates the size of all selected files added in a file input in bytes
 *
 * @example
 * <td-wb-file-upload [tdWbFileUploadMinValidator]="2" [(ngModel)]="files"></td-wb-file-upload>
 */
@Directive({
  selector: '[tdWbFileUploadMinValidator][ngModel]',
  providers: [FILE_UPLOAD_MIN_VALIDATOR]
})
export class FileUploadMinValidator implements Validator {
  // Typed as possibly-undefined so the `!== undefined` guard below is type-safe.
  private onChange?: () => void;
  readonly size: InputSignal<number | undefined> = input<number | undefined>(undefined, { alias: 'tdWbFileUploadMinValidator' });
  private _prevSize: number | undefined;

  constructor() {
    // effect: calls Angular Forms' registerOnValidatorChange() when constraint inputs change —
    // this directive uses effect() as a reactive alternative to implementing OnChanges.
    effect(() => {
      const s = this.size();
      if (s !== this._prevSize && this.onChange !== undefined) {
        this.onChange();
      }
      this._prevSize = s;
    });
  }

  validate(fileUploadControl: AbstractControl): ValidationErrors | null {
    const size = this.size();
    if (size !== undefined && fileUploadControl.value !== undefined && fileUploadControl.value !== null) {
      return this.validateMin(fileUploadControl.value, size);
    }
    return null;
  }

  registerOnValidatorChange(fn: () => void): void {
    this.onChange = fn;
  }

  private validateMin(files: Array<SelectedFile>, size: number): ValidationErrors | null {
    return files.length < size ? { MIN: true } : null;
  }
}
